
import { useState, useEffect, useRef } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "react-router-dom";
import { UsernameField } from "./form/UsernameField";
import { EmailField } from "./form/EmailField";
import { PasswordField } from "./form/PasswordField";
import { AuthSubmitButton } from "./form/AuthSubmitButton";
import { AuthToggle } from "./form/AuthToggle";
import { ForgotPasswordDialog } from "./form/ForgotPasswordDialog";
import { CaptchaWidget } from "./CaptchaWidget";
import { useTurnstileSiteKey } from "@/hooks/useTurnstileSiteKey";
import type { TurnstileInstance } from "@marsidev/react-turnstile";
import { validatePassword } from "@/utils/security";
import { Shield } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

interface AuthFormProps {
  isSignUp: boolean;
  isLoading: boolean;
  onSubmit: (email: string, password: string, username: string, captchaToken?: string) => Promise<void>;
  onToggleMode: () => void;
}

export const AuthForm = ({
  isSignUp,
  isLoading,
  onSubmit,
  onToggleMode,
}: AuthFormProps) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [username, setUsername] = useState("");
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [captchaToken, setCaptchaToken] = useState("");
  const captchaRef = useRef<TurnstileInstance>(null);
  const turnstileSiteKey = useTurnstileSiteKey();
  const { t } = useLanguage();
  const { toast } = useToast();
  const location = useLocation();

  // Password criteria state
  const passwordCriteria = {
    hasMinLength: password.length >= 8,
    hasUpperCase: /[A-Z]/.test(password),
    hasLowerCase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
  };

  useEffect(() => {
    if (isSignUp && password) {
      // Each required criterion is worth 20 points (5 criteria = 100 max)
      let strength = 0;
      if (passwordCriteria.hasMinLength) strength += 20;
      if (passwordCriteria.hasUpperCase) strength += 20;
      if (passwordCriteria.hasLowerCase) strength += 20;
      if (passwordCriteria.hasNumber) strength += 20;
      if (passwordCriteria.hasSpecialChar) strength += 20;
      
      setPasswordStrength(strength);
    } else {
      setPasswordStrength(0);
    }
  }, [password, isSignUp, passwordCriteria.hasMinLength, passwordCriteria.hasUpperCase, passwordCriteria.hasLowerCase, passwordCriteria.hasNumber, passwordCriteria.hasSpecialChar]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSignUp && password !== confirmPassword) {
      toast({
        title: t('error'),
        description: t('passwordsDoNotMatch'),
        variant: "destructive",
      });
      return;
    }
    
    // Additional password validation for signup
    if (isSignUp) {
      const passwordValidation = validatePassword(password);
      if (!passwordValidation.valid) {
        toast({
          title: t('passwordTooWeak'),
          description: passwordValidation.message,
          variant: "destructive",
        });
        return;
      }
    }
    
    if (turnstileSiteKey && !captchaToken) {
      toast({
        title: t('error'),
        description: 'Please complete the CAPTCHA challenge',
        variant: "destructive",
      });
      return;
    }

    try {
      await onSubmit(email, password, username, captchaToken || undefined);
    } catch (error) {
      // Error handling is managed by the parent component
    } finally {
      captchaRef.current?.reset();
      setCaptchaToken("");
    }
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength < 60) return "bg-red-500";
    if (passwordStrength < 100) return "bg-yellow-500";
    return "bg-green-500";
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-5">
        {isSignUp && (
          <UsernameField
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            label={t('username')}
            placeholder={t('enterUsername')}
            isDisabled={isLoading}
          />
        )}
        
        <EmailField
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          label={t('email')}
          placeholder={t('enterEmail')}
          isDisabled={isLoading}
        />
        
        <div className="space-y-2">
          <PasswordField
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            label={t('password')}
            placeholder={t('enterPassword')}
            isDisabled={isLoading}
          />
          
          {!isSignUp && (
            <div className="flex justify-end">
              <ForgotPasswordDialog />
            </div>
          )}
        </div>

        {isSignUp && (
          <>
            <PasswordField
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              label={t('confirmPassword')}
              placeholder={t('confirmPassword')}
              isDisabled={isLoading}
            />
            
            {password && (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-xs flex items-center gap-1">
                    <Shield size={12} />
                    {t('passwordStrength')}
                  </span>
                  <span className="text-xs">
                    {passwordStrength < 60 && t('weak')}
                    {passwordStrength >= 60 && passwordStrength < 100 && t('moderate')}
                    {passwordStrength === 100 && t('strong')}
                  </span>
                </div>
                <Progress 
                  value={passwordStrength} 
                  className={cn("h-1", getPasswordStrengthColor())}
                />
                
                {/* Password criteria checklist */}
                <div className="grid grid-cols-2 gap-1 text-xs">
                  <div className={cn("flex items-center gap-1", passwordCriteria.hasMinLength ? "text-green-600" : "text-muted-foreground")}>
                    {passwordCriteria.hasMinLength ? "✓" : "✗"} {t('passwordCriteria.minLength')}
                  </div>
                  <div className={cn("flex items-center gap-1", passwordCriteria.hasUpperCase ? "text-green-600" : "text-muted-foreground")}>
                    {passwordCriteria.hasUpperCase ? "✓" : "✗"} {t('passwordCriteria.uppercase')}
                  </div>
                  <div className={cn("flex items-center gap-1", passwordCriteria.hasLowerCase ? "text-green-600" : "text-muted-foreground")}>
                    {passwordCriteria.hasLowerCase ? "✓" : "✗"} {t('passwordCriteria.lowercase')}
                  </div>
                  <div className={cn("flex items-center gap-1", passwordCriteria.hasNumber ? "text-green-600" : "text-muted-foreground")}>
                    {passwordCriteria.hasNumber ? "✓" : "✗"} {t('passwordCriteria.number')}
                  </div>
                  <div className={cn("flex items-center gap-1", passwordCriteria.hasSpecialChar ? "text-green-600" : "text-muted-foreground")}>
                    {passwordCriteria.hasSpecialChar ? "✓" : "✗"} {t('passwordCriteria.special')}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        
        <CaptchaWidget ref={captchaRef} onVerify={setCaptchaToken} />

        <AuthSubmitButton
          isLoading={isLoading}
          isSignUp={isSignUp}
          loginText={t('login')}
          registerText={t('register')}
          loadingText={t('loading')}
        />
      </form>

      <AuthToggle
        isSignUp={isSignUp}
        onToggle={onToggleMode}
        loginText={t('login')}
        registerText={t('register')}
        alreadyHaveAccountText={t('alreadyHaveAccount')}
        dontHaveAccountText={t('dontHaveAccount')}
      />
    </>
  );
};
